import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  confirmInStorePayment,
  confirmInStorePaymentGripStaff,
  getActiveDiscountApi,
} from "../../../api/api";
import useRecaptcha from "../../../hooks/useRecaptcha";
import styles from "./PayInStoreButton.module.css";

const PayInStoreButton = ({
  bookings,
  customerId,
  customerName,
  pickupService,
  bookingDate,
  instoreLocation,
  agreedToTerms,
  strings = [],
  grips = [],
  onError,
}) => {
  const { getRecaptchaToken } = useRecaptcha();
  const [errorMessage, setErrorMessage] = useState("");
  const [showingToast, setShowingToast] = useState(false);
  const clearErrTimerRef = useRef(null);
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  const [activeDiscount, setActiveDiscount] = useState(null);

  useEffect(() => {
    const fetchDiscount = async () => {
      try {
        const response = await getActiveDiscountApi();
        if (response.data && response.data.active) {
          setActiveDiscount(response.data.discount);
        }
      } catch (error) {
        console.error("Error fetching active discount:", error);
      }
    };
    fetchDiscount();

    return () =>
      clearErrTimerRef.current && clearTimeout(clearErrTimerRef.current);
  }, []);

  if (pickupService !== "in-store") return null;

  const showError = (msg, { focusTerms = false } = {}) => {
    if (onError) {
      onError(msg, { focusTerms });
      return;
    }
    setErrorMessage(msg);
    setShowingToast(true);
    if (clearErrTimerRef.current) clearTimeout(clearErrTimerRef.current);
    clearErrTimerRef.current = setTimeout(() => {
      setShowingToast(false);
      setErrorMessage("");
    }, 3200);

    if (focusTerms) {
      const el = document.getElementById("terms-section");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("pulseHighlight");
        setTimeout(() => el.classList.remove("pulseHighlight"), 1600);
      }
    }
  };

  const getServiceKind = (b) => b.serviceKind || (b.gripId ? "grip" : "string");

  const getServiceSpeed = (b) =>
    getServiceKind(b) === "grip"
      ? b.gripType || "Regular"
      : b.stringType || "Regular";

  // ---- GRIP HELPERS ----
  const getGripCost = (b) => Number(b.gripCost ?? 0);
  const getGripServiceFee = (b) => Number(b.serviceFee ?? 0);
  const getGripTotal = (b) =>
    Number(b.totalCost ?? (getGripCost(b) + getGripServiceFee(b) || 0));

  // ---- STRING COST (RECALCULATED FROM INVENTORY) ----
  const getStringCostFromInventory = (booking) => {
    const stringId = booking.stringId ?? booking.string_id ?? null;
    if (!stringId) return 0;

    const stringDetail = strings.find((s) => s.string_id === stringId);
    if (!stringDetail) return 0;

    const serviceSpeed = getServiceSpeed(booking); // "Regular" or "Express"
    const isExpress = serviceSpeed === "Express";

    if (booking.isHybrid || booking.is_hybrid === 1) {
      const stringIdCross = booking.stringIdCross ?? booking.string_id_cross ?? null;
      const stringDetailCross = stringIdCross ? strings.find((s) => s.string_id === stringIdCross) : null;
      
      const isMainByos = stringDetail ? (stringDetail.name === "Bring Own String" || (stringDetail.name || "").toLowerCase().includes("bring own")) : true;
      const isCrossByos = stringDetailCross ? (stringDetailCross.name === "Bring Own String" || (stringDetailCross.name || "").toLowerCase().includes("bring own")) : true;
      const bothByos = isMainByos && isCrossByos;

      if (bothByos) {
        if (booking.freeService) {
          return isExpress ? 25 : 0;
        }
        if (activeDiscount) {
          return isExpress ? Number(activeDiscount.express_price) : Number(activeDiscount.regular_price);
        }
        return isExpress ? 100 : 75;
      }

      // Consolidated hybrid pricing: 200 regular / 225 express
      if (booking.freeService) {
        return isExpress ? 150 : 125;
      }
      if (activeDiscount) {
        const regDiscount = 75 - Number(activeDiscount.regular_price);
        const expDiscount = 100 - Number(activeDiscount.express_price);
        return isExpress ? Math.max(225 - expDiscount, 0) : Math.max(200 - regDiscount, 0);
      }
      return isExpress ? 225 : 200;
    }

    if (booking.freeService) {
      // Free stringing: base - 75, with +25 if Express
      return isExpress
        ? Math.max(stringDetail.basePrice - 75 + 25, 0)
        : Math.max(stringDetail.basePrice - 75, 0);
    } else {
      // Normal pricing
      if (activeDiscount) {
        return isExpress
          ? Math.max(stringDetail.basePrice - 75 + Number(activeDiscount.express_price), 0)
          : Math.max(stringDetail.basePrice - 75 + Number(activeDiscount.regular_price), 0);
      }
      return isExpress
        ? stringDetail.basePrice + 25
        : stringDetail.basePrice;
    }
  };

  const getIndividualCost = (b) => {
    const kind = getServiceKind(b);

    if (kind === "grip") {
      return getGripTotal(b);
    }

    // STRING: always recompute from inventory to avoid stale / zero totalCost
    const cost = getStringCostFromInventory(b);
    console.log("[getIndividualCost] string booking:", {
      booking: b,
      recomputedCost: cost,
    });
    return cost;
  };

  const handlePayInStore = async () => {
    if (!agreedToTerms) {
      showError("Please agree to the Terms & Conditions before proceeding.", {
        focusTerms: true,
      });
      return;
    }
    setIsProcessing(true);

    try {
      const token = await getRecaptchaToken("pay_in_store");
      if (!token) {
        showError("reCAPTCHA verification failed. Please try again.");
        setIsProcessing(false);
        return;
      }

      const allGrip = bookings.every((b) => getServiceKind(b) === "grip");

      const bookingRequests = bookings.map((booking) => {
        const serviceKind = getServiceKind(booking);
        const serviceSpeed = getServiceSpeed(booking);
        const totalCost = getIndividualCost(booking);

        const stringId = booking.stringId ?? booking.string_id ?? null;

        const payload = {
          bigcommerce_customer_id: customerId,
          service_kind: serviceKind,
          string_id: serviceKind === "string" ? stringId : null,
          grip_id: serviceKind === "grip" ? booking.gripId ?? null : null,
          booking_date: bookingDate,
          pickup_option: pickupService,
          delivery_address: null,
          racket_name: booking.racketName,
          // FINAL CHARGEABLE AMOUNT FOR THIS BOOKING
          total_cost: Number(totalCost),
          payment_option: "in-store",
          freeService: Boolean(booking.freeService),
          instore_location: instoreLocation || null,
          rp_id: booking.rp_id || null,
          service_speed: serviceSpeed,
          bc_sku: booking.bc_sku,
          bc_name: booking.bc_name ?? booking.gripName ?? "Grip",
          bc_brand: booking.bc_brand ?? null,
          bc_price_inc_tax: Number(
            booking.bc_price_inc_tax ?? booking.gripCost ?? 0
          ),
          tension_type: booking.tensionType || null,
          tension_main: booking.tensionType === "custom" && booking.tensionMain != null ? parseFloat(booking.tensionMain) : null,
          tension_cross: booking.tensionType === "custom" && booking.tensionCross != null ? parseFloat(booking.tensionCross) : null,
          tension_unit: booking.tensionType === "custom" ? (booking.tensionUnit || null) : null,
          prestretch: booking.tensionType === "custom" && booking.prestretch ? 1 : 0,
          prestretch_percent: booking.tensionType === "custom" && booking.prestretch && booking.prestretchPercent != null ? parseFloat(booking.prestretchPercent) : null,
        };

        if (serviceKind === "string") {
          const stringDetail = strings.find((s) => s.string_id === stringId);
          
          if (booking.isHybrid || booking.is_hybrid === 1) {
            const stringCrossId = booking.stringIdCross ?? booking.string_id_cross ?? null;
            const stringCross = strings.find((s) => s.string_id === stringCrossId);
            
            payload.string_name = stringDetail ? stringDetail.name : "Stringing Service";
            payload.string_brand = stringDetail ? stringDetail.brand : null;
            payload.string_price = 0;
            
            payload.is_hybrid = 1;
            payload.string_id_cross = stringCrossId;
            payload.string_name_cross = stringCross ? stringCross.name : null;
            payload.string_brand_cross = stringCross ? stringCross.brand : null;
            payload.string_price_cross = 0;
            payload.service_fee = 0;
            
            payload.prestretch_cross = booking.prestretchCross || booking.prestretch_cross ? 1 : 0;
            payload.prestretch_percent_cross = (booking.prestretchCross || booking.prestretch_cross) && (booking.prestretchPercentCross ?? booking.prestretch_percent_cross) != null ? parseFloat(booking.prestretchPercentCross ?? booking.prestretch_percent_cross) : null;
          } else {
            const finalStringCost = Number(totalCost);
            if (stringDetail) {
              payload.string_name = stringDetail.name;
              payload.string_brand = stringDetail.brand;
            } else {
              payload.string_name =
                booking.stringName || booking.string_name || null;
              payload.string_brand =
                booking.stringBrand || booking.string_brand || null;
            }
            payload.string_price = finalStringCost;
            payload.service_fee = 0;
            payload.is_hybrid = 0;
          }
        }

        if (serviceKind === "grip") {
          const gripDetail = grips.find((g) => g.grip_id === booking.gripId);
          if (gripDetail) {
            const gripCost = Number(gripDetail.bc_price_inc_tax || 0);

            payload.grip_cost = gripCost;
            payload.service_fee = 0; // no extra fee per booking, we already want total_cost = full cost
            payload.total_cost = gripCost;
            payload.bc_sku = gripDetail.bc_sku;
            payload.bc_name = gripDetail.bc_name;
            payload.bc_brand = gripDetail.bc_brand;
            payload.bc_price_inc_tax = gripCost;
          } else {
            payload.grip_cost = getGripCost(booking);
            payload.service_fee = getGripServiceFee(booking);
          }

          payload.bc_product_id =
            booking.bc_product_id ?? booking.product_id ?? null;
          payload.bc_variant_id =
            booking.bc_variant_id ?? booking.variant_id ?? null;
          payload.bc_sku =
            payload.bc_sku ?? booking.bc_sku ?? booking.sku ?? null;
          payload.bc_image_url =
            booking.bc_image_url ?? booking.image ?? null;
          payload.bc_name =
            payload.bc_name ?? booking.bc_name ?? booking.gripName ?? null;
        }

        return payload;
      });

      console.log("[PayInStoreButton] payload:", bookingRequests);

      const response = await (allGrip
        ? confirmInStorePaymentGripStaff(bookingRequests, token)
        : confirmInStorePayment(bookingRequests, token));

      console.log(
        "[PayInStoreButton] response:",
        response?.status,
        response?.data
      );

      navigate("/booking-success", {
        state: {
          message: "Your booking has been successfully confirmed!",
          bookingType: allGrip ? "grip" : "string",
        },
      });
    } catch (error) {
      console.error(
        "Error confirming in-store payment:",
        error?.response?.data || error
      );
      showError("Error processing Pay in Store request. Please try again.");
    } finally {
      setIsProcessing(false);
      console.log("Pay in Store process completed.");
    }
  };

  return (
    <>
      {isProcessing && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <div className={styles.loader}></div>
            <p>Please wait...</p>
            <p className="subText">Your booking is being confirmed.</p>
          </div>
        </div>
      )}
      {errorMessage && (
        <div
          className={`${styles.errorMessage} ${
            showingToast ? styles.toastVisible : ""
          }`}
          role="status"
          aria-live="polite"
        >
          {errorMessage}
        </div>
      )}

      <button
        className={styles.payInStoreButton}
        onClick={handlePayInStore}
        disabled={isProcessing}
        title={!agreedToTerms ? "You must agree to the Terms & Conditions" : ""}
      >
        Pay in Store
      </button>
    </>
  );
};

export default PayInStoreButton;
